import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  Bulb,
  Check,
  ChevronLeft,
  ChevronRight,
  Messages,
  Star,
} from '@saleshandy/icons';
import { IProps } from './types';
import Button from '../../../../../../../../shared/design-system/components/atoms/button';
import {
  customPlanFeature,
  customScalePlusFeatures,
  generateStepWisePlan,
  getButtonLabel,
  getEmailOutreachPlanYearlyPrice,
  getFestiveDiscountPercentage,
  getIsFestiveDiscountActive,
  getMonthlyPrice,
  getPlanDetailsObj,
  getPlanPrice,
  getPlansIcon,
} from './utils/helper';
import { SelectPlanCTAText } from '../../../../../../../../shared/design-system/components/molecules/pricing-card/types';
import { OverlayTooltip } from '../../../../../../../../shared/design-system/components/overlay';
import { BillingOption } from '../../../../../../../../shared/utils/billing-options';
import { RangeInput } from '../range-input';
import {
  getCustomScalePlusPlanName,
  isAnnuallyPlan,
  isQuarterlyPlan,
} from '../../utils/helper';
import {
  customPlan,
  isCustomScalePlusPlan,
} from '../plans-section/utils/helper';
import { isUserInPaidPlan } from '../../../../../../../../shared/design-system/components/molecules/subscription-plan-details/helper/helper';
import { accessibleOnClick } from '../../../../../../../../shared/utils/accessible-on-click';
import GlobalRestrictionModal from '../../../../../../../home/components/global-restriction-modal';
import { AssignmentType } from '../../../../../../../../shared/utils';
import UpdatePlanModal from '../update-plan-modal/update-plan-modal';

const OutreachPlanCard: React.FC<IProps> = ({
  plan,
  customScalePlusPlans,
  isPurchaseInProgress,
  handlePlanSelect,
  sendGetAccountSubscriptionResponse,
  sendGetPlansResponse,
  planCycle,
  isDemoAccount,
  onSchedule,
  isScheduleRequestLoading,
}) => {
  const { t } = useTranslation();

  const isFestiveDiscountActive = getIsFestiveDiscountActive();
  const festiveDiscountPercentage = getFestiveDiscountPercentage();

  //* State */
  const [rangeInputValue, setRangeInputValue] = useState<number[]>([1]);

  const [emailRangeInputValue, setEmailRangeInputValue] = useState<number[]>([
    1,
  ]);

  const isUserSubscribed = isUserInPaidPlan(
    sendGetAccountSubscriptionResponse?.planCode,
  );

  const [planDetails, setPlanDetails] = useState(
    getPlanDetailsObj(plan, isUserSubscribed),
  );

  const [showGlobalRestrictionModal, setShowGlobalRestrictionModal] = useState(
    false,
  );

  const [showUpdatePlanModal, setShowUpdatePlanModal] = useState(false);

  const dynamicPlans = plan?.assignmentType === AssignmentType.Dynamic;

  const onRangeInputChange = (v: number[]) => {
    setRangeInputValue(v);

    const isEmailLastRange = emailRangeInputValue[0] === 27;
    if (v[0] === 45 || isEmailLastRange) {
      setPlanDetails(getPlanDetailsObj(customPlan, isUserSubscribed));

      return;
    }

    //* Custom Scale Plus Plan */
    const scalePlusPlan = customScalePlusPlans.find(
      (p) =>
        p.planName ===
        getCustomScalePlusPlanName(v[0], isAnnuallyPlan(planCycle)),
    );

    if (scalePlusPlan) {
      setPlanDetails(getPlanDetailsObj(scalePlusPlan, isUserSubscribed));
    } else {
      setPlanDetails(getPlanDetailsObj(plan, isUserSubscribed));
    }
  };

  const onEmailRangeInputChange = (v: number[]) => {
    setEmailRangeInputValue(v);

    const isProspectLastRange = rangeInputValue[0] === 45;
    if (v[0] === 27 || isProspectLastRange) {
      setPlanDetails(getPlanDetailsObj(customPlan, isUserSubscribed));

      return;
    }

    /* Custom Scale Plus Plan */
    const scalePlusPlan = customScalePlusPlans.find(
      (p) =>
        p.planName ===
        getCustomScalePlusPlanName(v[0], isAnnuallyPlan(planCycle)),
    );

    if (scalePlusPlan) {
      setPlanDetails(getPlanDetailsObj(scalePlusPlan, isUserSubscribed));
    } else {
      setPlanDetails(getPlanDetailsObj(plan, isUserSubscribed));
    }
  };

  // Find the dynamic counts
  const dynamicCounts =
    sendGetPlansResponse?.find((plans) => plans.id >= 489)?.dynamicCounts || [];
  const prospects = dynamicCounts?.find(
    (count) => count?.label === 'Prospects',
  );
  const emails = dynamicCounts?.find((count) => count?.label === 'Emails');
  const stepWisePlanProspects = prospects
    ? generateStepWisePlan(
        prospects?.count?.min,
        prospects?.count?.max,
        prospects?.slabCount,
      )
    : {};
  const stepWisePlanEmails = emails
    ? generateStepWisePlan(
        emails?.count?.min,
        emails?.count?.max,
        emails?.slabCount,
      )
    : {};

  const getProspectsRangeValue =
    stepWisePlanProspects[`step${rangeInputValue[0]}`];

  const getEmailsRangeValue =
    stepWisePlanEmails[`step${emailRangeInputValue[0]}`];

  const prospectCount = Number(getProspectsRangeValue);
  const emailCount = Number(getEmailsRangeValue);

  const isCustomLabel =
    rangeInputValue[0] === 45 || emailRangeInputValue[0] === 27;

  const btnLabel = getButtonLabel({
    plan: planDetails,
    sendGetAccountSubscriptionResponse,
    sendGetPlansResponse,
    planCycle,
    prospectCountInputValue: prospectCount,
    emailCountInputValue: emailCount,
  });

  const getButtonLabelTxt = isCustomLabel
    ? SelectPlanCTAText.CustomPlan
    : btnLabel;

  const getButtonTheme = () => {
    if (
      planDetails.isBestValuePlan &&
      btnLabel !== SelectPlanCTAText.CurrentPlan &&
      btnLabel !== SelectPlanCTAText.DowngradePlan
    ) {
      return Button.Theme.Solid;
    }

    return Button.Theme.Ghost;
  };

  // Calculate the custom plan price as per the prospects and emails
  const getCustomPlanPrice = (
    billingOption: BillingOption,
    activeProspects: number,
    emailsPerMonth: number,
  ): number => {
    const planIds = {
      [BillingOption.MONTHLY]: 489,
      [BillingOption.QUARTERLY]: 490,
      [BillingOption.ANNUALLY]: 491,
    };

    const planId = planIds?.[billingOption];

    const planData = sendGetPlansResponse?.find(
      (planDataId) => planDataId?.id === planId,
    );

    const prospectRate =
      planData?.dynamicCounts?.find((label) => label?.label === 'Prospects')
        ?.perSlabAdditionalCost || 0;

    const emailRate =
      planData?.dynamicCounts?.find((label) => label?.label === 'Emails')
        ?.perSlabAdditionalCost || 0;

    // This is patch code for the custom plan price
    // Requirement - Update the pricing for custom plan with 100k prospects and 300k emails
    // This is done because we cannot change the perSlabAdditionalCost as it will affect all the combinations
    // So, we are returning the fixed price
    if (activeProspects === 100000 && emailsPerMonth === 300000) {
      if (billingOption === BillingOption.MONTHLY) {
        return 299;
      }
      if (billingOption === BillingOption.ANNUALLY) {
        return 209;
      }
    }

    // Calculate the cost
    const prospectCost = activeProspects * prospectRate;
    const emailCost = emailsPerMonth * emailRate;

    return prospectCost + emailCost;
  };

  const customMonthlyPrice = getCustomPlanPrice(
    BillingOption.MONTHLY,
    prospectCount,
    emailCount,
  );

  const customQuarterlyPrice = getCustomPlanPrice(
    BillingOption.QUARTERLY,
    prospectCount,
    emailCount,
  );

  const customYearlyPrice = getCustomPlanPrice(
    BillingOption.ANNUALLY,
    prospectCount,
    emailCount,
  );

  const customPrice = () => {
    if (isAnnuallyPlan(planCycle)) {
      if (isFestiveDiscountActive) {
        return customMonthlyPrice * (1 - festiveDiscountPercentage / 100);
      }
      return customYearlyPrice;
    }

    if (isQuarterlyPlan(planCycle)) {
      return customQuarterlyPrice;
    }

    return customMonthlyPrice;
  };

  const getEffectivePrice = () => {
    if (isFestiveDiscountActive && isAnnuallyPlan(planCycle)) {
      return (
        (getMonthlyPrice(planDetails?.displayName, sendGetPlansResponse) *
          festiveDiscountPercentage) /
        100
      );
    }
    return planDetails.price;
  };

  const discountAmount = () =>
    (getMonthlyPrice(planDetails.displayName, sendGetPlansResponse) -
      Math.ceil(getEffectivePrice())) *
    planDetails.planMode;

  const discountPriceYearly = () => {
    if (isFestiveDiscountActive) {
      return (
        (planDetails.price -
          Math.ceil((planDetails.price * festiveDiscountPercentage) / 100)) *
        12
      );
    }
    return (
      (planDetails.price -
        getPlanPrice(
          getEmailOutreachPlanYearlyPrice(
            planDetails.planCode,
            sendGetPlansResponse,
          ),
          12,
        )) *
      12
    );
  };

  const getCustomDiscountAmount = () => {
    if (isFestiveDiscountActive) {
      return (
        Math.ceil(customMonthlyPrice) -
        Math.ceil((customMonthlyPrice * festiveDiscountPercentage) / 100)
      );
    }
    return Math.ceil(customMonthlyPrice) - Math.ceil(customYearlyPrice);
  };

  const getPlanDiscountText = () => {
    if (isAnnuallyPlan(planCycle)) {
      return `Save $${
        dynamicPlans
          ? Math.ceil(getCustomDiscountAmount()) * planDetails?.planMode
          : discountAmount()
      } annually`;
    }
    if (isQuarterlyPlan(planCycle)) {
      return `Save $${
        dynamicPlans
          ? Math.ceil(getCustomDiscountAmount()) * planDetails?.planMode
          : discountAmount()
      } annually`;
    }
    return `Save $${
      dynamicPlans
        ? Math.ceil(getCustomDiscountAmount()) * 12
        : discountPriceYearly()
    } on annual plan`;
  };

  useEffect(() => {
    if (
      sendGetAccountSubscriptionResponse?.planMode === 12 &&
      dynamicPlans &&
      isCustomScalePlusPlan(sendGetAccountSubscriptionResponse?.planId)
    ) {
      onRangeInputChange([1]);
    }
  }, []);

  //* Class Variable */
  const cardClassName = classnames([
    'pricing-card',
    {
      'best-value-plan': planDetails.isBestValuePlan,
    },
  ]);

  const btnClassName = classnames([
    {
      'ghost-primary-new': !planDetails.isBestValuePlan,
    },
    {
      'ghost-primary-new-disable': btnLabel === SelectPlanCTAText.DowngradePlan,
    },
    {
      'solid-primary-new':
        btnLabel !== SelectPlanCTAText.CurrentPlan &&
        btnLabel !== SelectPlanCTAText.DowngradePlan &&
        planDetails.isBestValuePlan,
    },
    {
      'ghost-secondary-new-disable': btnLabel === SelectPlanCTAText.CurrentPlan,
    },
  ]);

  const features = (() => {
    if (isCustomLabel && dynamicPlans) return customPlanFeature;
    if (dynamicPlans) {
      return customScalePlusFeatures(
        getProspectsRangeValue,
        getEmailsRangeValue,
      );
    }
    return planDetails?.features || [];
  })();

  const getPlanLabel = () => {
    if (isCustomLabel && dynamicPlans) {
      return 'Custom';
    }
    if (dynamicPlans) {
      return 'Scale Plus';
    }
    return planDetails?.displayName;
  };

  const cardBtn = (
    <Button
      variant={Button.Variant.Primary}
      theme={getButtonTheme()}
      onClick={() => {
        if (isDemoAccount) {
          setShowGlobalRestrictionModal(true);
          return;
        }

        if (btnLabel === SelectPlanCTAText.UpdatePlan) {
          setShowUpdatePlanModal(true);
          return;
        }

        handlePlanSelect(planDetails?.id, planDetails?.planName, {
          prospectsCount: Number(getProspectsRangeValue),
          emailsCount: Number(getEmailsRangeValue),
        });
      }}
      className={`w-100 ${btnClassName}`}
      isLoading={isPurchaseInProgress}
      disabled={
        !['Contact Us', 'Custom']?.includes(getButtonLabelTxt) &&
        (btnLabel === SelectPlanCTAText.CurrentPlan ||
          btnLabel === SelectPlanCTAText.DowngradePlan ||
          isPurchaseInProgress)
      }
    >
      {getButtonLabelTxt}
    </Button>
  );

  return (
    <div className={cardClassName}>
      <div className="best-value-plan__labels-wrappers">
        {planDetails?.isBestValuePlan && (
          <div className="best-value-plan__labels">
            <Star color="#FFFFFF" />
            <span className="best-value-plan__labels-text">
              {t('labels.most_popular')}
            </span>
          </div>
        )}
      </div>
      <div className="out-reach-plans-card">
        <div className="card-body">
          <div className="pricing-card__details mb-2">
            <div className="pricing-card__details-header">
              <img
                height={40}
                width={40}
                src={getPlansIcon(planDetails.planName)}
                alt="building"
              />
              <span className="pricing-card__details-header-title">
                {getPlanLabel()}
              </span>
            </div>
            <div className="pricing-card__details-price-wrapper">
              {!isCustomLabel && planDetails?.price ? (
                <>
                  <div className="pricing-card__details-price-wrapper-currency">
                    <span className="pricing-card__details-price-wrapper-currency-amount">
                      $
                      {dynamicPlans
                        ? Math.ceil(customPrice())
                        : Math.ceil(getEffectivePrice())}
                    </span>
                    {(isAnnuallyPlan(planCycle) ||
                      isQuarterlyPlan(planCycle)) && (
                      <span className="pricing-card__details-price-wrapper-currency-amount-strikethrough">
                        $
                        {dynamicPlans
                          ? Math.ceil(customMonthlyPrice)
                          : getMonthlyPrice(
                              planDetails?.displayName,
                              sendGetPlansResponse,
                            )}
                      </span>
                    )}
                    <span className="pricing-card__details-price-wrapper-currency-amount-suffix">
                      /Month
                    </span>
                  </div>
                  <div className="pricing-card__details-price-wrapper-discount">
                    <span className="pricing-card__details-price-wrapper-discount-text">
                      {getPlanDiscountText()}
                    </span>
                  </div>
                </>
              ) : (
                <div className="mt-1">
                  <Messages
                    width={28}
                    height={28}
                    className="popover-arrow-color-txt"
                  />
                </div>
              )}
            </div>

            {btnLabel === SelectPlanCTAText.UpdatePlan ? (
              <OverlayTooltip
                text={
                  <div className="content">
                    Update your current plan to access the unlimited prospect
                    upload
                  </div>
                }
                className="update-plan-tooltip"
              >
                {cardBtn}
              </OverlayTooltip>
            ) : (
              cardBtn
            )}
          </div>

          <div className="pricing-card__features">
            {dynamicPlans && (
              <div className="range-wrap">
                <RangeInput
                  value={rangeInputValue}
                  onChange={onRangeInputChange}
                />
              </div>
            )}

            {dynamicPlans && (
              <div className="pricing-card__features-slider-control">
                <button
                  type="button"
                  className="pricing-card__features-slider-control-icon"
                  {...(rangeInputValue[0] > 1 &&
                    accessibleOnClick(() =>
                      onRangeInputChange([rangeInputValue[0] - 1]),
                    ))}
                >
                  <ChevronLeft color="#6b7280" />
                </button>
                <span className="pricing-card__features-slider-control-text">
                  <span className="font-semibold">
                    {getProspectsRangeValue?.toLocaleString()}{' '}
                  </span>
                  <span>Active Prospects</span>
                </span>
                <button
                  type="button"
                  className="pricing-card__features-slider-control-icon"
                  {...(rangeInputValue[0] < 45 &&
                    accessibleOnClick(() =>
                      onRangeInputChange([rangeInputValue[0] + 1]),
                    ))}
                >
                  <ChevronRight color="#6B7280" />
                </button>
              </div>
            )}

            {dynamicPlans && (
              <div className="range-wrap">
                <RangeInput
                  value={emailRangeInputValue}
                  onChange={onEmailRangeInputChange}
                  isExtendedRange={true}
                />
              </div>
            )}

            {dynamicPlans && (
              <div className="pricing-card__features-slider-control">
                <button
                  type="button"
                  className="pricing-card__features-slider-control-icon"
                  {...(emailRangeInputValue[0] > 1 &&
                    accessibleOnClick(() =>
                      onEmailRangeInputChange([emailRangeInputValue[0] - 1]),
                    ))}
                >
                  <ChevronLeft color="#6b7280" />
                </button>
                <span className="pricing-card__features-slider-control-text">
                  <span className="font-semibold">
                    {getEmailsRangeValue?.toLocaleString()}{' '}
                  </span>
                  <span>Email Send Per Month</span>
                </span>
                <button
                  type="button"
                  className="pricing-card__features-slider-control-icon"
                  {...(emailRangeInputValue[0] < 27 &&
                    accessibleOnClick(() =>
                      onEmailRangeInputChange([emailRangeInputValue[0] + 1]),
                    ))}
                >
                  <ChevronRight color="#6B7280" />
                </button>
              </div>
            )}

            <ul className="pricing-card__features-list">
              {features?.map(
                ({ label, labelClass, tooltipText, strikeText, highlight }) => (
                  <li
                    key={label}
                    className={classnames([
                      'pricing-card__features-list-item',
                      {
                        'bg-color': highlight,
                      },
                    ])}
                  >
                    <span className="pricing-card__features-list-item-check">
                      <Check className="check-icon-email-outreach" />
                    </span>
                    <>
                      {tooltipText ? (
                        <OverlayTooltip text={tooltipText}>
                          <span
                            className={classnames([
                              'pricing-card__features-list-item-label',
                              'underline-dashed',
                              labelClass,
                            ])}
                          >
                            {strikeText && (
                              <span className="regular-1 gray-txt-15 mr-1 text-decoration-line-through">
                                {strikeText}
                              </span>
                            )}
                            {label}
                          </span>
                        </OverlayTooltip>
                      ) : (
                        <span
                          className={classnames([
                            'pricing-card__features-list-item-label',
                            labelClass,
                          ])}
                        >
                          {strikeText && (
                            <span className="regular-1 gray-txt-15 mr-1 text-decoration-line-through">
                              {strikeText}
                            </span>
                          )}
                          {label}
                        </span>
                      )}
                    </>
                  </li>
                ),
              )}
            </ul>
          </div>
          {planDetails?.isBestValuePlan && (
            <div className="pricing-card__best-value-plan-insight">
              <Bulb color="#4F46E5" />
              <span className="pricing-card__best-value-plan-insight-text">
                {isAnnuallyPlan(planCycle)
                  ? t('labels.best_value_plan_insight_email_yearly')
                  : t('labels.best_value_plan_insight_email_monthly')}
              </span>
            </div>
          )}
        </div>
      </div>
      <GlobalRestrictionModal
        show={showGlobalRestrictionModal}
        onClose={() => setShowGlobalRestrictionModal(false)}
        title="You’re about to leave the demo account"
        content="Upgrading a plan requires a real Saleshandy account. You’ll be redirected to log in or sign up and continue from your actual account. Do you want to proceed?"
        redirectTo="billing/upgrade-plan/email-outreach"
      />
      <UpdatePlanModal
        show={showUpdatePlanModal}
        onCancel={() => setShowUpdatePlanModal(false)}
        onPayNow={() => {
          handlePlanSelect(planDetails?.id, planDetails?.planName, {
            prospectsCount: Number(getProspectsRangeValue),
            emailsCount: Number(getEmailsRangeValue),
          });
        }}
        onSchedule={onSchedule}
        subscriptionStatus={sendGetAccountSubscriptionResponse.status}
        isScheduleRequestLoading={isScheduleRequestLoading}
      />
    </div>
  );
};

export default OutreachPlanCard;
